import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Users, UserPlus, Activity, TrendingUp,
    Shield, ShieldOff, RefreshCw, Eye, Download,
    CheckCircle, Clock, Loader2, Image, Wifi, WifiOff
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api';
import webSocketService from '@/services/websocket';

const AdminDashboard = () => {
    const { user: admin } = useAuthStore();
    const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);

    // WebSocket connection status
    useEffect(() => {
        const socket = webSocketService.connect();
        if (socket) {
            setIsWebSocketConnected(webSocketService.isConnected());

            const handleConnect = () => setIsWebSocketConnected(true);
            const handleDisconnect = () => setIsWebSocketConnected(false);

            webSocketService.on('connect', handleConnect);
            webSocketService.on('disconnect', handleDisconnect);

            return () => {
                webSocketService.off('connect', handleConnect);
                webSocketService.off('disconnect', handleDisconnect);
            };
        }
    }, []);

    // Fetch dashboard stats
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['adminDashboard'],
        queryFn: async () => {
            const response = await api.get('/admin/dashboard');
            return response.data;
        },
        refetchInterval: 5 * 60 * 1000 // Refresh every 5 minutes
    });

    const stats = data?.stats;
    const recentActivity = data?.recentActivity;

    const StatCard = ({ title, value, icon: Icon, color, suffix = '' }) => {
        const colorClasses = {
            blue: 'bg-blue-100 text-blue-600',
            green: 'bg-green-100 text-green-600',
            purple: 'bg-purple-100 text-purple-600',
            yellow: 'bg-yellow-100 text-yellow-600',
            red: 'bg-red-100 text-red-600',
            gray: 'bg-gray-100 text-gray-600'
        };

        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        <p className="text-sm font-medium text-gray-600">{title}</p>
                        <p className="mt-2 text-3xl font-semibold text-gray-900">
                            {isLoading ? '-' : value?.toLocaleString() || 0}{suffix}
                        </p>
                    </div>
                    <div className={`p-3 rounded-full ${colorClasses[color]}`}>
                        <Icon className="h-6 w-6" />
                    </div>
                </div>
            </div>
        );
    };

    const UserItem = ({ user, label }) => (
        <div className="flex items-center justify-between py-2">
            <div className="flex items-center space-x-3">
                {user.profile?.profilePicture ? (
                    <img
                        src={`${import.meta.env.VITE_API_URL}${user.profile.profilePicture}`}
                        alt={user.profile?.name}
                        className="h-8 w-8 rounded-full object-cover"
                    />
                ) : (
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-600">
                            {user.profile?.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                    </div>
                )}
                <div>
                    <p className="text-sm font-medium text-gray-900">
                        {user.profile?.name || 'Unnamed User'}
                    </p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                </div>
            </div>
            <span className="text-xs text-gray-500">{label}</span>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">
                                Admin Dashboard
                            </h1>
                            <p className="mt-2 text-gray-600">
                                Welcome back, {admin?.name || 'Admin'}
                            </p>
                        </div>
                        <button
                            onClick={() => refetch()}
                            disabled={isLoading}
                            className="p-2 text-gray-600 hover:text-gray-700 bg-white border border-gray-300 rounded-md disabled:opacity-50"
                        >
                            <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <Link
                        to="/admin/users/create"
                        className="flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        <UserPlus className="h-5 w-5 mr-2" />
                        Create User
                    </Link>
                    <Link
                        to="/admin/users"
                        className="flex items-center justify-center px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <Users className="h-5 w-5 mr-2" />
                        Manage Users
                    </Link>
                    <Link
                        to="/admin/activity"
                        className="flex items-center justify-center px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <Activity className="h-5 w-5 mr-2" />
                        View Activity
                    </Link>
                    <button
                        onClick={handleExportUsers}
                        className="flex items-center justify-center px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        <Download className="h-5 w-5 mr-2" />
                        Export Users
                    </button>
                </div>

                {/* Main Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <StatCard
                        title="Total Users"
                        value={stats?.users?.total}
                        icon={Users}
                        color="blue"
                    />
                    <StatCard
                        title="Active Users"
                        value={stats?.users?.active}
                        icon={CheckCircle}
                        color="green"
                    />
                    <StatCard
                        title="Inactive Users"
                        value={stats?.users?.inactive}
                        icon={Clock}
                        color="red"
                    />
                    <StatCard
                        title="Total Admins"
                        value={stats?.admins?.total}
                        icon={Shield}
                        color="purple"
                    />
                </div>

                {/* Secondary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <StatCard
                        title="New Users Today"
                        value={stats?.users?.newToday}
                        icon={UserPlus}
                        color="green"
                    />
                    <StatCard
                        title="New Users This Week"
                        value={stats?.users?.newThisWeek}
                        icon={TrendingUp}
                        color="blue"
                    />
                    <StatCard
                        title="Profile Completion"
                        value={stats?.profileCompleteness?.average}
                        icon={CheckCircle}
                        color="purple"
                        suffix="%"
                    />
                </div>

                {/* System Status */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-600">Real-time Updates</p>
                                    <p className="mt-2 text-lg font-semibold">
                                        {isWebSocketConnected ? (
                                            <span className="text-green-600">Connected</span>
                                        ) : (
                                            <span className="text-red-600">Disconnected</span>
                                        )}
                                    </p>
                                </div>
                                <div className={`p-3 rounded-full ${
                                    isWebSocketConnected
                                        ? 'bg-green-100 text-green-600'
                                        : 'bg-red-100 text-red-600'
                                }`}>
                                    {isWebSocketConnected ? (
                                        <Wifi className="h-6 w-6" />
                                    ) : (
                                        <WifiOff className="h-6 w-6" />
                                    )}
                                </div>
                            </div>
                            <p className="mt-3 text-xs text-gray-500">
                                {isWebSocketConnected
                                    ? 'WebSocket connection is active. Real-time updates enabled.'
                                    : 'WebSocket connection is down. Please refresh the page.'
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* User Features Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <StatCard
                        title="Users with Profile Picture"
                        value={stats?.users?.withProfilePicture}
                        icon={Image}
                        color="yellow"
                    />
                    <StatCard
                        title="Users with Social Media"
                        value={stats?.users?.withSocialMedia}
                        icon={Activity}
                        color="blue"
                    />
                    <StatCard
                        title="Users with 2FA"
                        value={stats?.users?.withTwoFactor}
                        icon={Shield}
                        color="green"
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Recent Users */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Recent Users</h2>
                            <Link
                                to="/admin/users"
                                className="text-sm text-primary-600 hover:text-primary-700"
                            >
                                View all
                            </Link>
                        </div>
                        <div className="px-6 py-2">
                            {recentActivity?.newUsers && recentActivity.newUsers.length > 0 ? (
                                <div className="space-y-1">
                                    {recentActivity.newUsers.map((user) => (
                                        <UserItem
                                            key={user._id}
                                            user={user}
                                            label={new Date(user.createdAt).toLocaleDateString()}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-center py-8">
                                    No recent users
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Recent Logins */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-gray-900">Recent Logins</h2>
                            <Link
                                to="/admin/activity"
                                className="text-sm text-primary-600 hover:text-primary-700"
                            >
                                View activity
                            </Link>
                        </div>
                        <div className="px-6 py-2">
                            {recentActivity?.recentLogins && recentActivity.recentLogins.length > 0 ? (
                                <div className="space-y-1">
                                    {recentActivity.recentLogins.map((user, index) => (
                                        <UserItem
                                            key={`${user._id}-${index}`}
                                            user={user}
                                            label={new Date(user.lastLogin).toLocaleString()}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="text-gray-500 text-center py-8">
                                    No recent logins
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Quick Summary */}
                <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-blue-900 mb-3">System Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div>
                            <p className="text-blue-700">Active Rate</p>
                            <p className="text-xl font-semibold text-blue-900">
                                {stats?.users?.total > 0
                                    ? ((stats.users.active / stats.users.total) * 100).toFixed(1)
                                    : 0}%
                            </p>
                        </div>
                        <div>
                            <p className="text-blue-700">Profile Completion</p>
                            <p className="text-xl font-semibold text-blue-900">
                                {stats?.profileCompleteness?.average || 0}%
                            </p>
                        </div>
                        <div>
                            <p className="text-blue-700">2FA Adoption</p>
                            <p className="text-xl font-semibold text-blue-900">
                                {stats?.users?.total > 0
                                    ? ((stats.users.withTwoFactor / stats.users.total) * 100).toFixed(1)
                                    : 0}%
                            </p>
                        </div>
                        <div>
                            <p className="text-blue-700">Total Admins</p>
                            <p className="text-xl font-semibold text-blue-900">
                                {stats?.admins?.total || 0}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Export users function
    async function handleExportUsers() {
        try {
            const response = await api.get('/admin/users/export/csv', {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `users-export-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export users:', error);
        }
    }
};

export default AdminDashboard;